/**
 * The deliberately small Phase-1 intelligence vocabulary.
 *
 * This module freezes state and taxonomy names that persistence, HTTP and the
 * Owner workspace must agree on. It does not perform acquisition, inference,
 * scoring or recommendation generation; those are later pipeline stages.
 */
export const ANALYSIS_STAGES = [
    'INTAKE',
    'BUSINESS_EVIDENCE',
    'BUSINESS_PROFILE_REVIEW',
    'COMPETITOR_DISCOVERY',
    'COMPETITOR_CONFIRMATION',
    'TARGET_AND_COMPETITOR_CONTENT_COLLECTION',
    'SHALLOW_CONTENT_ENRICHMENT',
    'DEEP_CONTENT_UNDERSTANDING',
    'AUDIENCE_SIGNAL_EXTRACTION',
    'COMPARATIVE_ANALYSIS',
    'FINDING_CANDIDATES',
    'FINDINGS_REVIEW',
    'STRATEGIC_OPPORTUNITIES',
    'CREATIVE_DIRECTIONS',
    'COMPLETE',
];
export const ANALYSIS_EXECUTION_STATES = [
    'PENDING',
    'RUNNING',
    'AWAITING_REVIEW',
    'SUCCEEDED',
    'PARTIAL',
    'FAILED',
];
/** A valid analytical conclusion is separate from execution success/failure. */
export const ANALYSIS_OUTCOMES = ['SUPPORTED', 'INSUFFICIENT_EVIDENCE'];
export const REVIEW_ANALYSIS_STAGES = [
    'BUSINESS_PROFILE_REVIEW',
    'COMPETITOR_CONFIRMATION',
    'DEEP_CONTENT_UNDERSTANDING',
    'AUDIENCE_SIGNAL_EXTRACTION',
    'FINDINGS_REVIEW',
    'STRATEGIC_OPPORTUNITIES',
    'CREATIVE_DIRECTIONS',
];
export const BUSINESS_STATUSES = ['DRAFT', 'ACTIVE', 'ARCHIVED'];
export const BUSINESS_OFFER_TYPES = [
    'SERVICE',
    'PROGRAM',
    'PHYSICAL_PRODUCT',
    'DIGITAL_PRODUCT',
];
export const BUSINESS_SOURCE_PLATFORMS = ['WEBSITE', 'INSTAGRAM', 'TIKTOK'];
export const BUSINESS_SOURCE_RELATIONSHIPS = ['OWNED', 'DISCOVERED'];
export const CONTENT_SUBJECT_ROLES = ['TARGET', 'COMPETITOR'];
export const CONTENT_ENRICHMENT_TIERS = ['SHALLOW', 'DEEP'];
export const AUDIENCE_SIGNAL_KINDS = [
    'BOOKING_OR_COACHING_INTENT',
    'COMMERCIAL_ACTION_INTENT',
    'PRICE_QUESTION',
    'AVAILABILITY_OR_LOCATION_QUESTION',
    'SERVICE_OR_PROCESS_QUESTION',
    'SUBSTANTIVE_POSITIVE_RESPONSE',
    'OBJECTION_OR_FRICTION',
    'CONFUSION',
    'SPAM_OR_IRRELEVANT',
];
export const ANALYSIS_CONFIDENCE_LEVELS = ['LOW', 'MEDIUM', 'HIGH'];
export const AUDIO_MODES = [
    'VOICE',
    'MUSIC',
    'HYBRID',
    'AMBIENT',
    'NONE',
    'UNKNOWN',
];
export const BUSINESS_SOURCE_IDENTITY_BASES = [
    'SUBMITTED',
    'OBSERVED',
    'INFERRED',
    'UNCERTAIN',
];
export const EVIDENCE_OBSERVATION_BASES = [
    'OBSERVED',
    'BUSINESS_STATED',
    'MERCHANT_STATED',
    'MODEL_INFERRED',
    'OWNER_CONFIRMED',
    'UNKNOWN',
];
export const BUSINESS_EVIDENCE_TYPES = [
    'business_identity',
    'source_identity',
    'offer',
    'audience',
    'customer_need',
    'delivery_mode',
    'geography',
    'positioning',
    'proof_asset',
    'price',
    'conversion_path',
    'contact_method',
    'claim',
    'unknown',
];
export const ANALYSIS_READINESS_STATES = [
    'READY',
    'NEEDS_REVIEW',
    'NOT_READY',
];
export const PROVIDER_COST_BASES = ['EXACT', 'ESTIMATED', 'UNKNOWN'];
const singletonEvidenceTypes = new Set(['business_identity']);
function canonicalEvidenceValue(value) {
    return value
        .normalize('NFKC')
        .trim()
        .replace(/[._@—–-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .toLocaleLowerCase('ar-SA');
}
function strongestBasis(observations) {
    const strength = {
        OWNER_CONFIRMED: 5,
        OBSERVED: 4,
        BUSINESS_STATED: 3,
        MERCHANT_STATED: 3,
        MODEL_INFERRED: 2,
        UNKNOWN: 1,
    };
    return observations.reduce((winner, observation) => strength[observation.basis] > strength[winner] ? observation.basis : winner, 'UNKNOWN');
}
function weakestConfidence(observations) {
    const values = observations
        .map((entry) => entry.confidence)
        .filter((value) => value !== null);
    if (values.length === 0)
        return null;
    if (values.includes('LOW'))
        return 'LOW';
    if (values.includes('MEDIUM'))
        return 'MEDIUM';
    return 'HIGH';
}
function businessIdentityValuesCompatible(left, right) {
    const normalizedLeft = canonicalEvidenceValue(left);
    const normalizedRight = canonicalEvidenceValue(right);
    const includesPhrase = (value, phrase) => ` ${value} `.includes(` ${phrase} `);
    return (normalizedLeft === normalizedRight ||
        includesPhrase(normalizedLeft, normalizedRight) ||
        includesPhrase(normalizedRight, normalizedLeft));
}
/**
 * Conservative deterministic fusion: exact normalized equivalents are joined;
 * unlike values survive as separate facts and, for singleton concepts, become
 * reviewable conflicts. No source wins merely because it was processed later.
 */
export function fuseBusinessEvidence(observations) {
    const grouped = new Map();
    for (const observation of observations) {
        if (observation.basis === 'UNKNOWN' || observation.type === 'unknown')
            continue;
        const canonical = canonicalEvidenceValue(observation.value);
        if (canonical.length === 0)
            continue;
        const key = `${observation.type}:${canonical}`;
        const entries = grouped.get(key) ?? [];
        entries.push(observation);
        grouped.set(key, entries);
    }
    const facts = [...grouped.entries()].map(([key, entries]) => {
        const first = entries[0];
        return {
            key,
            type: first.type,
            value: first.value.trim(),
            basis: strongestBasis(entries),
            confidence: weakestConfidence(entries),
            observationIds: [...new Set(entries.map((entry) => entry.id))],
            sourceIds: [...new Set(entries.map((entry) => entry.sourceId))],
            sourceUrls: [...new Set(entries.map((entry) => entry.sourceUrl))],
            freshestObservedAt: entries
                .map((entry) => entry.observedAt)
                .sort()
                .at(-1),
        };
    });
    const conflicts = [];
    for (const type of singletonEvidenceTypes) {
        const candidates = facts.filter((fact) => fact.type === type);
        if (candidates.length < 2)
            continue;
        if (type === 'business_identity' &&
            candidates.every((left) => candidates.every((right) => businessIdentityValuesCompatible(left.value, right.value)))) {
            continue;
        }
        conflicts.push({
            type,
            factKeys: candidates.map((fact) => fact.key),
            values: candidates.map((fact) => fact.value),
            reason: `Sources provide ${candidates.length} distinct ${type.replaceAll('_', ' ')} values.`,
        });
    }
    const seenTypes = new Set(facts.map((fact) => fact.type));
    const unknownTypes = BUSINESS_EVIDENCE_TYPES.filter((type) => type !== 'unknown' && !seenTypes.has(type));
    return {
        schemaVersion: 'business-evidence-fusion.v1',
        facts,
        conflicts,
        unknownTypes,
    };
}
export function decideAnalysisReadiness(observations, fusion = fuseBusinessEvidence(observations)) {
    const usableSourceCount = new Set(observations
        .filter((entry) => entry.type !== 'unknown' && entry.value.trim().length > 0)
        .map((entry) => entry.sourceId)).size;
    const offers = fusion.facts.filter((fact) => fact.type === 'offer');
    const claims = fusion.facts.filter((fact) => fact.type === 'claim');
    const identities = fusion.facts.filter((fact) => fact.type === 'business_identity' || fact.type === 'source_identity');
    const reasons = [];
    if (usableSourceCount === 0)
        reasons.push('No source produced usable evidence.');
    if (identities.length === 0)
        reasons.push('Business identity is not resolvable.');
    if (claims.length === 0)
        reasons.push('Business category is not resolvable.');
    if (offers.length === 0)
        reasons.push('No concrete public Offer was observed.');
    if (usableSourceCount === 0 ||
        identities.length === 0 ||
        claims.length === 0 ||
        offers.length === 0) {
        return {
            schemaVersion: 'analysis-readiness.v1',
            state: 'NOT_READY',
            reasons,
            usableSourceCount,
            offerCount: offers.length,
            identityCount: identities.length,
        };
    }
    if (fusion.conflicts.length > 0) {
        return {
            schemaVersion: 'analysis-readiness.v1',
            state: 'NEEDS_REVIEW',
            reasons: fusion.conflicts.map((conflict) => conflict.reason),
            usableSourceCount,
            offerCount: offers.length,
            identityCount: identities.length,
        };
    }
    return {
        schemaVersion: 'analysis-readiness.v1',
        state: 'READY',
        reasons: [
            'A resolvable identity, business category, usable source and concrete Offer are present.',
        ],
        usableSourceCount,
        offerCount: offers.length,
        identityCount: identities.length,
    };
}
function fieldFromFact(fact) {
    if (fact === undefined) {
        return {
            value: null,
            basis: 'UNKNOWN',
            confidence: null,
            evidenceIds: [],
            reviewState: 'UNKNOWN',
        };
    }
    return {
        value: fact.value,
        basis: fact.basis,
        confidence: fact.confidence,
        evidenceIds: fact.observationIds,
        reviewState: 'PROPOSED',
    };
}
function fieldsOfType(fusion, type) {
    return fusion.facts.filter((fact) => fact.type === type).map(fieldFromFact);
}
function isGiftCardOffer(value) {
    return /\bgift (?:card|voucher)s?\b|بطاقات? هدايا|قسائم? هدايا/iu.test(canonicalEvidenceValue(value));
}
function inferredOfferCategory(value, deliveryMode) {
    const normalized = canonicalEvidenceValue(value);
    if (/برنامج|program|خطة|plan/.test(normalized))
        return 'PROGRAM';
    if (isGiftCardOffer(value) ||
        /digital|download|template|ebook|رقمي|تحميل|قالب/.test(normalized)) {
        return 'DIGITAL_PRODUCT';
    }
    if (deliveryMode.value === 'Physical product delivery')
        return 'PHYSICAL_PRODUCT';
    if (/makeup|beauty|skincare|perfume|fragrance|cookie|dessert|coffee|clothing|shoes|accessories|gift|مكياج|تجميل|عناية|عطر|كوكيز|حلويات|قهوة|ملابس|أحذية|إكسسوارات|هدايا/.test(normalized)) {
        return 'PHYSICAL_PRODUCT';
    }
    return 'SERVICE';
}
/** A deliberately small, evidence-bound draft; no free-form model output. */
export function draftBusinessProfile(fusion, sourceArtifactIds) {
    const first = (type) => fusion.facts.find((fact) => fact.type === type);
    const audience = fieldsOfType(fusion, 'audience');
    const needs = fieldsOfType(fusion, 'customer_need');
    const geography = fieldFromFact(first('geography'));
    const conversion = fieldsOfType(fusion, 'conversion_path');
    const proof = fieldsOfType(fusion, 'proof_asset');
    const prices = fieldsOfType(fusion, 'price');
    const delivery = fieldsOfType(fusion, 'delivery_mode');
    const rawOfferFacts = fusion.facts.filter((fact) => fact.type === 'offer');
    const uniqueOfferFacts = rawOfferFacts.filter((fact, index) => rawOfferFacts.findIndex((candidate) => canonicalEvidenceValue(candidate.value) ===
        canonicalEvidenceValue(fact.value)) === index);
    const identity = first('business_identity') ?? first('source_identity');
    const claim = first('claim');
    const profileLabels = [identity?.value, claim?.value]
        .filter((value) => value !== undefined)
        .map(canonicalEvidenceValue);
    const combinedLabels = new Set([
        profileLabels.join(' '),
        [...profileLabels].reverse().join(' '),
    ]);
    const concreteOfferFacts = uniqueOfferFacts.filter((fact) => {
        const value = canonicalEvidenceValue(fact.value);
        return !profileLabels.includes(value) && !combinedLabels.has(value);
    });
    const offerFacts = (concreteOfferFacts.length > 0 ? concreteOfferFacts : uniqueOfferFacts.slice(0, 1)).slice(0, 8);
    const physicalDelivery = delivery.find((field) => field.value === 'Physical product delivery');
    const conversionMatchesOffer = (field, offerValue) => {
        if (field.value === null || offerValue === null)
            return false;
        const offerKey = canonicalEvidenceValue(offerValue);
        const offerKeys = [offerKey, offerKey.replace(/s$/u, '')];
        try {
            const pathKey = canonicalEvidenceValue(decodeURIComponent(new URL(field.value).pathname));
            return offerKeys.some((key) => pathKey.includes(key));
        }
        catch {
            return false;
        }
    };
    const offers = offerFacts
        .map((fact, index) => {
        const title = fieldFromFact(fact);
        const deliveryMode = isGiftCardOffer(fact.value)
            ? fieldFromFact(undefined)
            : (physicalDelivery ??
                delivery[index] ??
                delivery[0] ??
                fieldFromFact(undefined));
        const categoryValue = inferredOfferCategory(fact.value, deliveryMode);
        const matchingConversion = conversion.find((field) => conversionMatchesOffer(field, fact.value));
        return {
            localId: `offer-${index + 1}`,
            title,
            category: {
                value: categoryValue,
                basis: 'MODEL_INFERRED',
                confidence: 'MEDIUM',
                evidenceIds: fact.observationIds,
                reviewState: 'PROPOSED',
            },
            description: title,
            audienceOrNeed: audience[0] ?? needs[0] ?? fieldFromFact(undefined),
            deliveryMode,
            geographyOrAccess: geography,
            price: prices[index] ?? prices[0] ?? fieldFromFact(undefined),
            proof,
            conversionPath: matchingConversion ??
                conversion[index] ??
                conversion[0] ??
                fieldFromFact(undefined),
        };
    })
        .filter((offer, index, entries) => {
        const path = offer.conversionPath.value;
        return (path === null ||
            !conversionMatchesOffer(offer.conversionPath, offer.title.value) ||
            entries.findIndex((candidate) => candidate.conversionPath.value === path &&
                conversionMatchesOffer(candidate.conversionPath, candidate.title.value)) === index);
    });
    return {
        schemaVersion: 'business-profile.v1',
        identity: fieldFromFact(first('business_identity') ?? first('source_identity')),
        niche: fieldFromFact(first('claim')),
        geography,
        audience,
        customerNeeds: needs,
        positioning: fieldFromFact(first('positioning')),
        proofAssets: proof,
        conversionPaths: conversion,
        constraintsAndUnknowns: fusion.unknownTypes.map((type) => type.replaceAll('_', ' ')),
        offers,
        conflicts: fusion.conflicts,
        sourceArtifactIds: [...new Set(sourceArtifactIds)],
    };
}
/**
 * These are field names, not closed value enums. Values remain bounded text
 * until real fitness fixtures show which concepts are stable enough to close.
 */
export const BUSINESS_PROFILE_DIMENSIONS = [
    'business_identity',
    'offers',
    'offer_category',
    'customer_need',
    'audience',
    'delivery_mode',
    'geography',
    'positioning',
    'proof_assets',
    'conversion_or_booking_path',
    'business_objective',
    'constraints',
];
export const CONTENT_FEATURE_DIMENSIONS = [
    'format',
    'hook_mechanism',
    'message_or_angle',
    'content_archetype',
    'proof_mechanism',
    'offer_visibility',
    'call_to_action',
    'emotional_framing',
    'language_or_dialect',
    'audio_mode',
    'product_or_service_shown',
    'production_complexity',
];
export const FITNESS_PHASE_ONE_TAXONOMY = {
    version: 'fitness-taxonomy.v0',
    businessProfileDimensions: BUSINESS_PROFILE_DIMENSIONS,
    contentFeatureDimensions: CONTENT_FEATURE_DIMENSIONS,
    audienceSignals: AUDIENCE_SIGNAL_KINDS,
    audioModes: AUDIO_MODES,
    confidenceLevels: ANALYSIS_CONFIDENCE_LEVELS,
};
const stageSet = new Set(ANALYSIS_STAGES);
const executionStateSet = new Set(ANALYSIS_EXECUTION_STATES);
const outcomeSet = new Set(ANALYSIS_OUTCOMES);
const offerTypeSet = new Set(BUSINESS_OFFER_TYPES);
const sourcePlatformSet = new Set(BUSINESS_SOURCE_PLATFORMS);
const sourceRelationshipSet = new Set(BUSINESS_SOURCE_RELATIONSHIPS);
export function isAnalysisStage(value) {
    return typeof value === 'string' && stageSet.has(value);
}
export function isAnalysisExecutionState(value) {
    return typeof value === 'string' && executionStateSet.has(value);
}
export function isAnalysisOutcome(value) {
    return typeof value === 'string' && outcomeSet.has(value);
}
export function isBusinessOfferType(value) {
    return typeof value === 'string' && offerTypeSet.has(value);
}
export function isBusinessSourcePlatform(value) {
    return typeof value === 'string' && sourcePlatformSet.has(value);
}
export function isBusinessSourceRelationship(value) {
    return typeof value === 'string' && sourceRelationshipSet.has(value);
}
export function isReviewAnalysisStage(stage) {
    return REVIEW_ANALYSIS_STAGES.includes(stage);
}
export function nextAnalysisStage(stage) {
    const index = ANALYSIS_STAGES.indexOf(stage);
    return index === ANALYSIS_STAGES.length - 1
        ? null
        : (ANALYSIS_STAGES[index + 1] ?? null);
}
/** Only the immediate successor is legal; no stage can be skipped. */
export function canAdvanceAnalysisStage(current, proposed) {
    return nextAnalysisStage(current) === proposed;
}
/**
 * One attempt is append-only once terminal. A retry is a later attempt row,
 * never a resurrection or overwrite of the failed one.
 */
export function canTransitionAnalysisExecutionState(current, proposed, stage) {
    if (current === 'PENDING')
        return proposed === 'RUNNING';
    if (current === 'RUNNING') {
        return (proposed === 'SUCCEEDED' ||
            proposed === 'PARTIAL' ||
            proposed === 'FAILED' ||
            (proposed === 'AWAITING_REVIEW' && isReviewAnalysisStage(stage)));
    }
    if (current === 'AWAITING_REVIEW') {
        return proposed === 'SUCCEEDED' || proposed === 'PARTIAL' || proposed === 'FAILED';
    }
    return false;
}
//# sourceMappingURL=analysis.js.map